import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AuthTemplatePreview from '../../../components/WhatsApp/AuthTemplatePreview';
import WhatsAppTemplatePreview from '../../../components/WhatsApp/TemplatePreview';
import VariablePreview from '../../../components/WhatsApp/VariablePreview';
import { getRequest, postRequest } from '../../../helpers/apiHandler';
import { applyTextFormat } from '../../../helpers/textFormatHelper';
import { showAlert } from '../../../store/actions/alertAction';

const WhatsAppTemplateCreator = () => {
  const { waba_id, phone_number_id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { access_token } = useSelector((state) => state.auth.user);

  const [template, setTemplate] = useState({
    name: '',
    language: '',
    category: '',
    components: [],
  });

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [templateHeader, setTemplateHeader] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateFooter, setTemplateFooter] = useState('');
  const [templateButtons, setTemplateButtons] = useState([]);
  const [bodyVariables, setBodyVariables] = useState([]);
  const [bodyVariableEntries, setBodyVariableEntries] = useState([]);
  const [codeExp, setCodeExp] = useState(5);

  const bodyTextareaRef = useRef(null);

  // Constants based on Meta's guidelines
  const categories = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'AUTHENTICATION', label: 'Authentication' },
    { value: 'UTILITY', label: 'Utility' },
  ];

  const buttonTypes = [
    { value: 'QUICK_REPLY', label: 'Quick Reply' },
    { value: 'URL', label: 'Call to Action (URL)' },
    { value: 'PHONE_NUMBER', label: 'Phone Number' },
    { value: 'OTP', label: 'One-Time Password' },
  ];

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = () => {
    const requestData = { headers: { Authorization: access_token.token } };
    getRequest('/v2/whatsapp/wa-template-languages', requestData, (res) => {
      if (res.success) {
        setLanguages(res.data.languages);
      }
    });
  };

  const detectVariables = (text) => {
    const variablePattern = /\{\{\d+\}\}/g;
    return text.match(variablePattern) || [];
  };

  useEffect(() => {
    setBodyVariables(detectVariables(templateBody));
  }, [templateBody]);

  const handleInputChange = (field, value) => {
    setTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddVariable = useCallback(() => {
    const newVariableNumber = bodyVariables.length + 1;
    const newVariable = `{{${newVariableNumber}}}`;
    setBodyVariables([...bodyVariables, newVariable]);
    setTemplateBody(`${templateBody}${newVariable}`);
  }, [bodyVariables, templateBody]);

  const handleRemoveVariable = useCallback(
    (index) => {
      const newBodyVariables = bodyVariables.filter((_, i) => i !== index);
      setBodyVariables(newBodyVariables);

      const newTemplateBody = templateBody.replace(`{{${index + 1}}}`, '');
      setTemplateBody(newTemplateBody);
    },
    [bodyVariables, templateBody]
  );

  const handleBodyVariableInputChange = useCallback((index, value) => {
    setBodyVariableEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      newEntries[index] = value;
      return newEntries;
    });
  }, []);

  const handleAddButton = useCallback(() => {
    if (templateButtons.length < 3) {
      setTemplateButtons((prev) => [
        ...prev,
        {
          type: 'QUICK_REPLY',
          text: '',
          url: '',
          phoneNumber: '',
        },
      ]);
    }
  }, [templateButtons]);

  const handleButtonChange = useCallback((index, field, value) => {
    setTemplateButtons((prevButtons) => {
      const newButtons = [...prevButtons];
      newButtons[index] = {
        ...newButtons[index],
        [field]: value,
      };
      return newButtons;
    });
  }, []);

  const handleRemoveButton = useCallback((index) => {
    setTemplateButtons((prevButtons) => prevButtons.filter((_, i) => i !== index));
  }, []);

  const validateButton = (button) => {
    switch (button.type) {
      case 'URL':
        return button.url && button.url.startsWith('https://');
      case 'PHONE_NUMBER':
        return button.phoneNumber && /^\+[1-9]\d{1,14}$/.test(button.phoneNumber);
      default:
        return true;
    }
  };

  const validateTemplate = () => {
    if (!template.name || !template.language || !template.category) {
      dispatch(showAlert('Please fill in all required fields', 'danger'));
      return false;
    }

    if (!templateBody.trim()) {
      dispatch(showAlert('Body text is required', 'danger'));
      return false;
    }

    // Authentication template specific validation
    if (template.category === 'AUTHENTICATION') {
      if (!bodyVariables.length) {
        dispatch(
          showAlert(
            'Authentication templates must include at least one variable for the code',
            'danger'
          )
        );
        return false;
      }

      if (!templateButtons.length) {
        dispatch(showAlert('Authentication templates must include an OTP button', 'danger'));
        return false;
      }

      const hasValidOTPButton = templateButtons.some(
        (btn) =>
          btn.type === 'OTP' &&
          (btn.otp_type === 'COPY_CODE' ||
            (btn.otp_type === 'ONE_TAP' &&
              Array.isArray(btn.supported_apps) &&
              btn.supported_apps.every((app) => app.package_name && app.signature_hash)))
      );

      if (!hasValidOTPButton) {
        dispatch(
          showAlert('Authentication templates must have a valid OTP button configuration', 'danger')
        );
        return false;
      }

      if (codeExp < 1 || codeExp > 60) {
        dispatch(showAlert('Code expiration must be between 1 and 60 minutes', 'danger'));
        return false;
      }
    } else {
      // Non-authentication template validation
      if (templateButtons.length > 0) {
        const invalidButton = templateButtons.find((button) => !validateButton(button));
        if (invalidButton) {
          dispatch(
            showAlert(`Invalid ${invalidButton.type.toLowerCase()} button configuration`, 'danger')
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateTemplate()) return;

    let requestData = {};
    if (template.category === 'AUTHENTICATION') {
      requestData = {
        waba_id: waba_id,
        name: template.name,
        language: selectedLanguage[0].value,
        category: template.category,
        components: [
          templateBody && {
            type: 'BODY',
            text: templateBody,
            add_security_recommendation: true,
          },
          {
            type: 'FOOTER',
            text: `Your code will expire in ${codeExp} minutes`,
            code_expiration_minutes: codeExp,
          },
          templateButtons.length > 0 && {
            type: 'BUTTONS',
            buttons: templateButtons,
          },
        ].filter(Boolean),
      };
    } else {
      requestData = {
        waba_id: waba_id,
        name: template.name,
        language: selectedLanguage[0].value,
        category: template.category,
        components: [
          templateHeader && {
            type: 'HEADER',
            format: 'TEXT',
            text: templateHeader,
          },
          templateBody && {
            type: 'BODY',
            text: templateBody,
            ...(bodyVariables.length > 0 && {
              example: {
                body_text: [bodyVariableEntries.map((entry) => entry)],
              },
            }),
          },
          templateFooter && { type: 'FOOTER', text: templateFooter },
          templateButtons.length > 0 && {
            type: 'BUTTONS',
            buttons: templateButtons.map((button) => ({
              type: button.type,
              text: button.text,
              ...(button.type === 'URL' && { url: button.url }),
              ...(button.type === 'PHONE_NUMBER' && { phone_number: button.phoneNumber }),
            })),
          },
        ].filter(Boolean),
      };
    }

    postRequest('/v2/whatsapp/wa-template-create', requestData, access_token.token, (res) => {
      if (res.success) {
        dispatch(showAlert('Template created successfully', 'success'));
        navigate(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`);
      } else {
        dispatch(showAlert(res.message || 'Failed to create template', 'danger'));
      }
    });
  };

  const handleTextFormat = useCallback(
    (formatType) => {
      const textarea = bodyTextareaRef.current;
      if (!textarea) return;

      const result = applyTextFormat({
        text: templateBody,
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
        formatType,
      });

      if (result) {
        setTemplateBody(result.text);

        // Restore selection
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
        }, 0);
      }
    },
    [templateBody]
  );

  return (
    <div className="container-fluid p-4">
      <Row>
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="bg-success text-white p-2 rounded mr-2">
                  <i className="fas fa-comment"></i>
                </div>
                <h5 className="mb-0">Create New Template</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Category */}
                <Form.Group className="mb-4">
                  <Form.Label>Category</Form.Label>
                  <Select
                    values={categories.filter((c) => c.value === template.category)}
                    onChange={(values) => handleInputChange('category', values[0]?.value)}
                    options={categories}
                    placeholder="Select category"
                  />
                </Form.Group>

                {/* Basic Information */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Template Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={template.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter template name"
                        maxLength={512}
                      />
                      <Form.Text className="text-muted">
                        {template.name.length}/512 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Language</Form.Label>
                      <Select
                        values={selectedLanguage}
                        onChange={(values) => {
                          setSelectedLanguage(values);
                          handleInputChange('language', values[0]?.value);
                        }}
                        options={languages}
                        placeholder="Select language"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Template Content */}
                <div className="mb-4">
                  <h6 className="text-dark">Template Content</h6>
                  <p className="text-muted small">
                    Fill out the provided form fields for your template.
                  </p>

                  {/* Header */}
                  {template.category !== 'AUTHENTICATION' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>
                          Header <span className="text-muted">• Optional</span>
                        </span>
                        <small className="text-muted">{templateHeader.length}/60</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={templateHeader}
                        onChange={(e) => setTemplateHeader(e.target.value)}
                        placeholder="Enter header text"
                        maxLength={60}
                      />
                    </Form.Group>
                  )}

                  {/* Body */}
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex justify-content-between">
                      <span>Body</span>
                      <small className="text-muted">{templateBody.length}/1024</small>
                    </Form.Label>
                    <Form.Control
                      ref={bodyTextareaRef}
                      as="textarea"
                      rows={4}
                      value={templateBody}
                      onChange={(e) => setTemplateBody(e.target.value)}
                      placeholder="Enter message body"
                      maxLength={1024}
                    />
                    <div className="mt-2 btn-toolbar">
                      <Button
                        variant="light"
                        size="sm"
                        className="me-1"
                        onClick={() => handleTextFormat('bold')}
                        title="Make text bold (*text*)"
                      >
                        <strong>B</strong>
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="me-1"
                        onClick={() => handleTextFormat('italic')}
                        title="Make text italic (_text_)"
                      >
                        <i>I</i>
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="me-1"
                        onClick={() => handleTextFormat('strikethrough')}
                        title="Make text strikethrough (~text~)"
                      >
                        <s>S</s>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleAddVariable}
                        className="ms-2"
                      >
                        Add Variable
                      </Button>
                    </div>
                  </Form.Group>

                  {/* Variables */}
                  {bodyVariables.length > 0 && (
                    <div className="mb-3">
                      <h6>Variable Examples</h6>
                      {bodyVariables.map((variable, index) => (
                        <Row key={index} className="mb-2 align-items-end">
                          <Col>
                            <Form.Group className="mb-0">
                              <Form.Label>Example for {variable}</Form.Label>
                              <Form.Control
                                type="text"
                                value={bodyVariableEntries[index] || ''}
                                onChange={(e) =>
                                  handleBodyVariableInputChange(index, e.target.value)
                                }
                                placeholder={`Enter example value for ${variable}`}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs="auto" className="ps-0">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveVariable(index)}
                              style={{ height: '38px' }}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  {template.category !== 'AUTHENTICATION' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>
                          Footer <span className="text-muted">• Optional</span>
                        </span>
                        <small className="text-muted">{templateFooter.length}/60</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={templateFooter}
                        onChange={(e) => setTemplateFooter(e.target.value)}
                        placeholder="Enter footer text"
                        maxLength={60}
                      />
                    </Form.Group>
                  )}

                  {/* Authentication Template Specific */}
                  {template.category === 'AUTHENTICATION' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Code Expiration (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={codeExp}
                        onChange={(e) => setCodeExp(parseInt(e.target.value))}
                        min={1}
                        max={60}
                      />
                    </Form.Group>
                  )}

                  {/* Buttons */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Buttons</h6>
                      {templateButtons.length < 3 && (
                        <Button variant="outline-secondary" size="sm" onClick={handleAddButton}>
                          Add Button
                        </Button>
                      )}
                    </div>
                    {templateButtons.map((button, index) => (
                      <Row key={index} className="mb-2 align-items-end">
                        <Col>
                          <Form.Group className="mb-0">
                            <Form.Label>Button {index + 1}</Form.Label>
                            <Form.Group>
                              <Form.Label>Button Type</Form.Label>
                              <Select
                                values={buttonTypes.filter((bt) => bt.value === button.type)}
                                onChange={(values) =>
                                  handleButtonChange(index, 'type', values[0]?.value)
                                }
                                options={buttonTypes}
                                placeholder="Select button type"
                              />
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>Button Text</Form.Label>
                              <Form.Control
                                type="text"
                                value={button.text}
                                onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                placeholder="Enter button text"
                                maxLength={20}
                              />
                            </Form.Group>

                            {button.type === 'URL' && (
                              <Form.Group>
                                <Form.Label>URL</Form.Label>
                                <Form.Control
                                  type="url"
                                  value={button.url}
                                  onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                                  placeholder="https://example.com"
                                />
                                <Form.Text className="text-muted">
                                  Must start with https://
                                </Form.Text>
                              </Form.Group>
                            )}

                            {button.type === 'PHONE_NUMBER' && (
                              <Form.Group>
                                <Form.Label>Phone Number</Form.Label>
                                <Form.Control
                                  type="tel"
                                  value={button.phoneNumber}
                                  onChange={(e) =>
                                    handleButtonChange(index, 'phoneNumber', e.target.value)
                                  }
                                  placeholder="+1234567890"
                                />
                                <Form.Text className="text-muted">
                                  Include country code (e.g., +1 for US)
                                </Form.Text>
                              </Form.Group>
                            )}
                            {button.type === 'OTP' && (
                              <Form.Group>
                                <Form.Label>OTP Type</Form.Label>
                                <Select
                                  values={[
                                    { value: 'COPY_CODE', label: 'Copy Code' },
                                    { value: 'ONE_TAP', label: 'One Tap' },
                                  ].filter((bt) => bt.value === button.otp_type)}
                                  onChange={(values) =>
                                    handleButtonChange(index, 'otp_type', values[0]?.value)
                                  }
                                  options={[
                                    { value: 'COPY_CODE', label: 'Copy Code' },
                                    { value: 'ONE_TAP', label: 'One Tap' },
                                  ]}
                                  placeholder="Select OTP type"
                                />
                              </Form.Group>
                            )}
                            {button.type === 'OTP' && button.otp_type === 'ONE_TAP' && (
                              <Form.Group>
                                <Form.Label>Supported Apps</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={button.supported_apps}
                                  onChange={(e) =>
                                    handleButtonChange(index, 'supported_apps', e.target.value)
                                  }
                                  placeholder="Enter supported apps"
                                />
                                <Form.Text className="text-muted">
                                  Enter package name and signature hash separated by comma (e.g.,
                                  com.example.app, signature_hash)
                                </Form.Text>
                              </Form.Group>
                            )}
                          </Form.Group>
                        </Col>
                        <Col xs="auto" className="ps-0">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveButton(index)}
                            style={{ height: '38px', width: '38px' }}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <Button variant="primary" type="submit">
                    Create Template
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <div className="sticky-top" style={{ top: '1rem' }}>
            {template.category === 'AUTHENTICATION' ? (
              <AuthTemplatePreview
                body={templateBody}
                codeExpiration={codeExp}
                buttons={templateButtons}
              />
            ) : (
              <WhatsAppTemplatePreview
                header={templateHeader}
                body={templateBody}
                footer={templateFooter}
                buttons={templateButtons}
              />
            )}

            {bodyVariables.length > 0 && (
              <VariablePreview variables={bodyVariables} examples={bodyVariableEntries} />
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default WhatsAppTemplateCreator;
